import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import ClockIcon from "@/assets/icons/misc/clock-icon.tsx"
import EmptyCalendarIcon from "@/assets/icons/misc/empty-calendar-icon.tsx"
import RoundedCheckDone from "@/assets/icons/misc/rounded-check-done.tsx"
import RoundedCheck from "@/assets/icons/misc/rounded-check.tsx"
import { useGetAnimalsQuery } from "@/features/farm/api/animals-api.ts"
import { getTaskState, TASK_STATES } from "@/features/tasks"
import { useToggleTaskStatusMutation } from "@/features/tasks/api/tasks-api.ts"
import TaskCardPillAnimalInfo from "@/features/tasks/components/task-card-pills/task-card-pill-animal-info.tsx"
import TaskCardPillCategoryInfo from "@/features/tasks/components/task-card-pills/task-card-pill-category-info.tsx"
import { useGetTaskCategory } from "@/features/tasks/hooks/use-get-task-category.ts"
import { useGetTaskDateHour } from "@/features/tasks/hooks/use-get-task-date-hour.ts"
import { TASKS_LIST_VIEW } from "@/features/tasks/routes/route-list.ts"
import { type Task } from "@/features/tasks/types/task.ts"
import { useAppDispatch, useAppSelector } from "@/redux/hooks.ts"
import { setSelectedTasksIds } from "@/redux/slices/task-selection-slice.ts"
import { cn } from "@/utils/styles.ts"

interface TaskCardProps {
  task: Task
}

const TaskCard: React.FC<TaskCardProps> = ({ task }) => {
  const { t } = useTranslation()
  const { formattedDate, formattedHour } = useGetTaskDateHour(task)
  const navigate = useNavigate()
  const { data = [] } = useGetAnimalsQuery()
  const [toggleTaskStatus] = useToggleTaskStatusMutation()
  const dispatch = useAppDispatch()
  const selectedTasks = useAppSelector(
    (state) => state.taskSelectionSlice.selectedTasksIds,
  )
  const isSelectionEnabled = useAppSelector(
    (state) => state.taskSelectionSlice.isSelectionEnabled,
  )
  const isSelected = (task: Task) => {
    return selectedTasks.includes(task.id)
  }

  const taskState = getTaskState(task, isSelected(task))

  const taskCategory = useGetTaskCategory({ task })
  const taskAnimals = data?.filter((animal) =>
    task.animals?.includes(animal.id),
  )

  const handleToggleTaskStatus = (
    task: Task,
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.stopPropagation()
    event.preventDefault()

    if (isSelectionEnabled) {
      handleTaskSelection(event)
      return
    }

    toast.promise(toggleTaskStatus(task.id).unwrap(), {
      pending: t("updatingTask"),
      success: t("taskUpdated"),
      error: t("errorUpdatingTask"),
    })
  }

  const handleTaskSelection = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLDivElement>,
  ) => {
    event.preventDefault()
    event.stopPropagation()
    if (!isSelectionEnabled) {
      navigate(`${TASKS_LIST_VIEW}/${task.id}`)
      return
    }

    if (isSelected(task)) {
      dispatch(
        setSelectedTasksIds(selectedTasks.filter((id) => id !== task.id)),
      )
    } else {
      dispatch(setSelectedTasksIds([...selectedTasks, task.id]))
    }
  }

  const hourStyle =
    taskState === TASK_STATES.PAST ? "text-red-500" : "text-neutral-700"

  const capitalizedTitle =
    task.title.charAt(0).toUpperCase() + task.title.slice(1)

  const isMetadata = task.gestation_calendar_entry_metadata

  const totalEntities =
    task.animals?.length +
    task.crop_plantings?.length +
    task.queens?.length +
    task.hives?.length +
    task.parcels?.length +
    task.apiaries.length +
    task.birds_groups?.length +
    task.queens?.length +
    task.hives?.length +
    task.parcels?.length +
    task.apiaries.length +
    task.birds_groups?.length

  const moreEntitiesCondition =
    task.animals && task.animals.length != totalEntities && totalEntities > 0

  const animalEntitiesCondition =
    taskAnimals &&
    taskAnimals.length > 0 &&
    task.animals.length == totalEntities

  return (
    <div
      onClick={handleTaskSelection}
      className={cn(
        "box-border grid h-fit w-full flex-grow-0 rounded-[12px] border border-neutral-200 bg-white",
        taskState === TASK_STATES.SELECTED &&
          "border border-brown-100 bg-brown-50 text-brown-500",
        taskState === TASK_STATES.PAST && "border-red-400 bg-white",
      )}
    >
      <div className="flex min-h-[100px] w-full flex-col justify-between pb-2.5 pl-4 pr-0">
        <div
          className={"grid"}
          style={{
            gridTemplateColumns: isMetadata
              ? "calc(100% - 1px) 1px"
              : "calc(100% - 60px) 60px",
          }}
        >
          <div
            className={
              "box-border flex h-[60px] max-w-full items-center overflow-hidden text-ellipsis whitespace-nowrap pr-2 text-neutral-900"
            }
          >
            <p className={"max-w-full truncate text-[16px]"}>
              {capitalizedTitle}
            </p>
            {Object.hasOwn(task, "hasChanges") && (
              <i className={"text-[10px]"}>{t("unsavedChangesIndicator")}</i>
            )}
          </div>
          {!isMetadata && (
            <div
              className={"h-fit w-[60px] p-4"}
              onClick={(e) => handleToggleTaskStatus(task, e)}
            >
              {task.is_done ? (
                <RoundedCheckDone className={"h-[25px] w-[25px]"} />
              ) : (
                <RoundedCheck className={"h-[25px] w-[25px]"} />
              )}
            </div>
          )}
        </div>

        {/* Bottom part of the card */}
        <div
          className={cn(
            "flex items-start justify-between gap-4 pr-4",
            taskCategory &&
              !animalEntitiesCondition &&
              !moreEntitiesCondition &&
              "justify-start",
          )}
        >
          {formattedDate && formattedHour && (
            <div
              className={
                "flex h-full items-center justify-center gap-2 font-normal"
              }
            >
              <EmptyCalendarIcon
                size={12}
                color={
                  taskState === TASK_STATES.PAST
                    ? "var(--color-red-500)"
                    : undefined
                }
              />
              <p
                className={cn(
                  "flex items-center gap-[10px] text-[12px]",
                  hourStyle,
                )}
              >
                {formattedDate}
                <div className={"flex items-center gap-1"}>
                  <ClockIcon className={cn("h-3 w-3", hourStyle)} />{" "}
                  {formattedHour}
                </div>
              </p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {taskCategory && (
              <TaskCardPillCategoryInfo taskCategory={taskCategory} />
            )}
            {animalEntitiesCondition && (
              <TaskCardPillAnimalInfo animals={taskAnimals} />
            )}
            {moreEntitiesCondition && (
              <div
                className={
                  "box-border grid h-[30px] w-fit max-w-full items-center rounded-[15px] border border-neutral-200 bg-white px-2.5"
                }
              >
                {totalEntities}
                {" " + t("entities")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default TaskCard
